import { $Qll, $Q } from '../utils/query-selector';
import { stringToHTML } from '../utils/to-html';
import api from '../services/api';
import { initialInfinityScroll } from './infinity-scroll';
import { defultSortBy } from './sort-by';
import { openAccordion } from '../utils/accordion';
import { rangeFilters } from './filters-range';
import { initializeMesAlertsMethods } from './mes-alerts';
import { mediaQuery } from '../utils/media-query';
import { toggleDataActive } from '../utils/toggle-dataset';

const ACC_ACTIVE_SESSION_STORAGE_ITEM = 'accordion-filters-status';

/**
 * Use regex to search and clean repeatd characteres
 * @param {string} url - Filters URL
 * @returns clean URL
 */
export const cleanURL = (url) => {
  const regex = /([&?])+/g;
  return url.replace(regex, '$1');
};

/**
 * Store of method to remove filters
 */
const removeCasesMethodStore = {
  remove: (newFilter, triggerData) => {
    const filtersOfProducts = $Q(
      `#js-filter-form .form__checkbox[data-value="${triggerData}"]`,
      stringToHTML(newFilter)
    );

    if (filtersOfProducts) {
      $Q(
        `.filter-group-display__list-item[data-value="${triggerData}"]`
      ).innerHTML = filtersOfProducts.outerHTML;
    }
  },
  remove_all: () => {
    removeCasesMethodStore.remove_session_storage();
  },
  remove_session_storage: () => {
    const priceApplied = sessionStorage.getItem('price-filter');
    if (priceApplied) sessionStorage.removeItem('price-filter');
  },
};

/**
 * Update browser url without reload page
 * @param {string} currentUrl - Root of url
 * @param {string} url - Query url
 * @author Daniel Santos
 */
function updateURL(currentUrl, url) {
  // To clean url of characteres duplicate
  window.history.pushState(
    null,
    '',
    `${window.location.origin}${currentUrl}?${cleanURL(url)}`
  );
}

function navigationScroll() {
  const collectionWrap = $Q('.collection__container');

  collectionWrap.scrollIntoView({ block: 'start', behavior: 'smooth' });
}

/**
 * Render section with filters applied
 * @param {string} newFilter - New HTML to update page
 * @param {string} typeFeature - If it is remove
 * @param {string} triggerData - Input value to get input node
 * @see removeCasesMethodStore
 * @author Daniel Santos
 */
function renderSections(newFilter, typeFeature, triggerData) {
  const listOfProducts = $Q('#js-filter-rendering', stringToHTML(newFilter));
  const tagsOfProducts = $Q(
    '.collection-filters__controls',
    stringToHTML(newFilter)
  );
  const filtersComponent = $Q('#js-filter-form', stringToHTML(newFilter));
  const emptyState = $Q('.search__empty', stringToHTML(newFilter));

  if (!listOfProducts && emptyState) {
    $Q('.js-empty-state ').innerHTML = emptyState.outerHTML;

    if (typeFeature)
      removeCasesMethodStore[typeFeature](newFilter, triggerData);

    navigationScroll();

    return false;
  } else if (listOfProducts) {
    $Q('.collection__products-grid').innerHTML = listOfProducts.outerHTML;
    $Q('.collection-filters').innerHTML = tagsOfProducts.outerHTML;

    mediaQuery('(min-width: 768px)', () => {
      accordionInitialStatus(filtersComponent);
      keepAccordionStatus(filtersComponent);
    });

    mediaQuery('(max-width: 767px)', () => {
      accordionInitialStatus(filtersComponent);
    });

    $Q('#filter').innerHTML = filtersComponent.outerHTML;

    mediaQuery('(min-width: 768px)', () => {
      keepAccordionStatus($Q('#js-filter-form'), 'position');
    });

    if (typeFeature)
      removeCasesMethodStore[typeFeature](newFilter, triggerData);

    navigationScroll();

    return true;
  }
}

/**
 * Get accordions type saved in session storage.
 * Set status to accordion was actived before section rendering
 * @param {node} filtersComponent
 * @param {string} action To add accordion status or accordion scroll position
 * @returns null - stop called
 */
function keepAccordionStatus(filtersComponent, action = 'status') {
  const { accordionsStatusActive, filterScrollposition } = JSON.parse(
    window.sessionStorage.getItem(ACC_ACTIVE_SESSION_STORAGE_ITEM)
  );

  if (!accordionsStatusActive.length) return;

  if (action === 'status') {
    accordionsStatusActive.forEach((typeFilter) => {
      const item = $Q(
        `[data-type-acc="${typeFilter.typeAcc}"] .accordion-item__button`,
        filtersComponent
      );

      if (item) {
        item.dataset.active = 'true';
      }
    });
  } else {
    accordionsStatusActive.forEach((typeFilter) => {
      const itemContent = $Q(
        `[data-type-acc="${typeFilter.typeAcc}"] .accordion-item__content`,
        filtersComponent
      );

      if (itemContent) {
        itemContent.scrollTop = typeFilter.positionScroll;
      }
    });

    if ($Q('#filter') && filterScrollposition) {
      $Q('#filter').scrollTop = filterScrollposition;
    }
  }
}

/**
 * Iterate each accordion filter and set initial status to mobile
 * @param {node} filtersComponent
 */
function accordionInitialStatus(filtersComponent) {
  const items = $Qll(
    `[data-type-acc] .accordion-item__button`,
    filtersComponent
  );

  if (items.length) {
    items.forEach((item) => {
      item.dataset.active = 'false';
    });
  }
}

/**
 * Get section data
 * @param {string} currentUrl - Root of url
 * @param {string} urlParams - Query url
 * @returns {string} - Response object
 * @author Daniel Santos
 */
async function fetchSection(currentUrl, urlParams) {
  const filtersURL = await api.shopifySectionByUrl(
    `${window.location.origin}${currentUrl}`,
    `${currentUrl === '/search' ? 'main-search' : 'collection-products'}`,
    `?${urlParams}&section_id`
  );

  return filtersURL;
}

/**
 * Request news sections based on filters
 * And initit main methods
 * @param {string} currentUrl - section URL
 * @param {string} searchParams - Query
 * @param {string} typeFeature - If it is remove
 * @param {string} triggerData - Input value to get input node
 * @param {object} config
 * @author Daniel Santos
 */
export async function initializeMethods(config) {
  const {
    currentUrl,
    searchParams,
    typeFeature,
    triggerData,
    triggerMobileApply,
  } = config;

  saveAccordionStatus();

  const data = await fetchSection(currentUrl, cleanURL(searchParams));

  if (data) {
    const emptyState = renderSections(data, typeFeature, triggerData);
    updateURL(currentUrl, searchParams);
    createLoader('remove');
    mobileApply(triggerMobileApply);

    if (!emptyState) return;

    initialInfinityScroll();
    removeFilters();
    defultSortBy();
    rangeFilters();
    filtersSearching();
    initDeskMethods();
    initMobileMethods();

    // openAccordion method must be after init methods
    openAccordion('.accordions.filter-group', false);

    // Mes alerts component
    initializeMesAlertsMethods();
  }
}

const mobileApply = (btn) => {
  if (!btn) return;

  mediaQuery('(max-width: 767px)', () => {
    if (
      btn.id === 'js-apply-filters-mobile-btn' &&
      $Q('#filter') &&
      $Q('#overlay--filter')
    ) {
      $Q('#filter').dataset.active = false;
      $Q('#overlay--filter').remove();
      document.body.style.overflow = 'initial';
    }
  });
};

const initDeskMethods = () => {
  mediaQuery('(min-width: 768px)', handleInputsForm);
};

const initMobileMethods = () => {
  mediaQuery('(max-width: 767px)', () => {
    handleInputsFormClick();

    toggleDataActive('#filter-open', '#filter', {
      overlay: true,
      type: 'side-cart',
      blockScroll: true,
    });

    toggleDataActive('#filter-close', '#filter', {
      overlay: true,
      type: 'side-cart',
      blockScroll: true,
    });
  });
};

/**
 * Validate if there are some query of pagination and remove
 */
export function removePagination() {
  const paginationParams = new URLSearchParams(window.location.search);

  if (paginationParams.has('page')) {
    paginationParams.delete('page');
  }
}

/**
 * Validate if there is sort by and get it to add to new URL
 * @returns {string} - Query sort by structure
 */
function getAndSaveSortBy() {
  let saveSortBy = new URLSearchParams(window.location.search);

  if (saveSortBy.has('sort_by')) {
    return `sort_by=${saveSortBy.get('sort_by')}`;
  }
}

/**
 * Validate input price for that it isn't render with select another filters
 * @param {string} inputType - Input type
 * @param {object} formData - Form structure
 * @author Daniel Santos
 */
function validatePriceFilter(inputType, formData) {
  const MIN_INPUT_NAME = 'filter.v.price.gte';
  const MAX_INPUT_NAME = 'filter.v.price.lte';
  const priceApplied = sessionStorage.getItem('price-filter');

  if (inputType !== 'range' && inputType !== 'number') {
    if (!priceApplied) {
      if (formData.has(MIN_INPUT_NAME) || formData.has(MAX_INPUT_NAME)) {
        formData.delete(MIN_INPUT_NAME);
        formData.delete(MAX_INPUT_NAME);
      }
    }
  } else {
    sessionStorage.setItem('price-filter', '1');
  }
}

/**
 * This method is only to desktop
 * Listen change event
 * Get formData with the class FormData
 * with formData structures URL with the class URLSearchParams
 * Also, add sort by query to URL
 * @see getAndSaveSortBy
 * @returns {null} stop called
 * @author Daniel Santos
 */
export function handleInputsForm() {
  const form = $Q('#js-filter-form');

  if (!form) return;

  removePagination();

  form.addEventListener('change', async (e) => {
    // Block filters searching's input to this method
    if (e.target.classList.contains('js-filters-searching')) return;

    const formData = new FormData(e.target.closest('form'));

    validatePriceFilter(e.target.type, formData);

    const searchParamsUrl = new URLSearchParams(formData);
    createLoader();
    structureParams(searchParamsUrl);

    //> Filters
    const typeList = [];
    const valueList = [];
    const hardwareList = [];
    let minPrice = 0;
    let maxPrice = 0;
    let condition = '';
    let edition = '';
    let material = '';
    let productionYear = '';
    let size = '';
    formData.forEach(function (value, key) {
      const element = $Q(`input[name="${key}"]`);
      typeList.push(element.dataset.name);
      valueList.push(value);
      if (key.includes('price.gte'))
        minPrice = value.replace('.', '').replace(',', '');
      if (key.includes('price.lte'))
        maxPrice = value.replace('.', '').replace(',', '');
      if (key.includes('custom.product_condition')) condition = value;
      if (key.includes('custom.editions')) edition = value;
      if (key.includes('custom.hardware')) hardwareList.push(value);
      if (key.includes('custom.material_filtres')) material = value;
      if (key.includes('custom.volume')) size = value;
      if (key.includes('custom.annee_de_production_filtres'))
        productionYear = value.replace(/^\D+/g, '');
    });

    console.log('filter_interaction');
    dataLayer.push({
      event: 'filter_interaction',
      filter_type: typeList,
      selected_value: valueList,
      min_price: Number(minPrice),
      max_price: Number(maxPrice),
      condition: condition,
      limited_edition: edition,
      other_color: hardwareList,
      material: material,
      size: size,
      production_year: productionYear,
    });
  });

  filtersScrollup();
}

/**
 * This method is only to mobile
 * Listen click event
 * Get formData with the class FormData
 * with formData structures URL with the class URLSearchParams
 * Also, add sort by query to URL
 * @returns
 */
export function handleInputsFormClick() {
  const btnToApply = $Qll('.accordions.filter-group .accordion-item__button');
  const form = $Q('#js-filter-form');
  btnToApply.push($Q('#js-apply-filters-mobile-btn')); // Add second trigger to apply filters

  let formDataSaved = window.location.search
    ? window.location.search.split('?')[1]
    : '';
  formDataSaved = formDataSaved.split('&sort_by=')[0]; // Get current search url to compare
  formDataSaved =
    formDataSaved[formDataSaved.length - 1] === '&'
      ? formDataSaved.slice(0, formDataSaved.length - 1)
      : formDataSaved; // Get current search url to compare

  if (!form) return;

  removePagination();

  if (!btnToApply.length) return;

  btnToApply.forEach((btn) => {
    btn.addEventListener('click', async (e) => {
      if (
        btn.dataset.active == 'true' ||
        btn.id === 'js-apply-filters-mobile-btn'
      ) {
        const formData = new FormData(form);
        const searchParamsUrl = new URLSearchParams(formData);

        // Compare current search params with new search param and stop or continue
        if (searchParamsUrl.toString() === formDataSaved) {
          mobileApply(btn);
          return;
        }

        createLoader();
        structureParams(
          searchParamsUrl,
          btn.id === 'js-apply-filters-mobile-btn' && btn
        );
      }
    });
  });
}

/**
 * Create params' structure to filters
 * Initialize next method initializeMethods
 * @see initializeMethods
 * @param {object} searchParamsUrl - new URLSearchParams object
 * @param {node} btn - trigger mobile apply to close filters aside
 * @returns null - stop called
 */
const structureParams = async (searchParamsUrl, triggerMobileApply = null) => {
  const config = $Q('#data-config');

  if (!config) return;

  const { currentUrl, currentUrlSearch } = config.dataset;

  if (!currentUrl) return;

  const saveSortBy = getAndSaveSortBy();
  let searchParams = searchParamsUrl.toString();
  searchParams = `${searchParams}&${saveSortBy ? `&${saveSortBy}` : ''}`;

  if (currentUrlSearch.length) {
    searchParams = `${searchParams}${
      saveSortBy ? `&${saveSortBy}` : ''
    }&${currentUrlSearch}`;
  }

  searchParams = cleanURL(searchParams);
  config.dataset.currentUrlFilters = searchParams;
  await initializeMethods({ currentUrl, searchParams, triggerMobileApply });
};

/**
 * Create and render loader component as a overlay
 * @param {string} action action type
 * @returns null - stop called
 */

const createLoader = (action = 'create') => {
  const idOverlay = `overlay--loader-filters`;
  const node = $Q('.collection__filters');

  if (!node) return;
  const parent = node.parentNode;

  if (action === 'create' && parent) {
    const overlay = document.createElement('div');
    const loader = document.createElement('div');

    overlay.setAttribute('id', idOverlay);
    overlay.classList.add('overlay', 'overlay--loader-filters');
    loader.classList.add('loading');
    overlay.append(loader);

    parent.insertBefore(overlay, node);
  }

  if (action === 'remove' && parent) {
    if ($Q(`#${idOverlay}`)) parent.removeChild($Q(`#${idOverlay}`));
  }
};

/**
 * Get accordions with status active.
 * Save accordion type in an array.
 * Save array in session storage
 * Only accordions with status active will save in sesion storage
 */
function saveAccordionStatus() {
  const accordion = $Qll('#js-filter-form .accordion-item');

  if (!accordion) return;

  const accordionsStatusActive = accordion
    .filter((accFilter) => {
      const itemButton = $Q('.accordion-item__button', accFilter);

      if (itemButton.dataset.active === 'true') {
        return accFilter;
      }
    })
    .map((el) => {
      const itemContent = $Q('.accordion-item__content', el);

      return {
        typeAcc: el.dataset.typeAcc,
        positionScroll: itemContent ? itemContent.scrollTop : 0,
      };
    });

  window.sessionStorage.setItem(
    ACC_ACTIVE_SESSION_STORAGE_ITEM,
    JSON.stringify({
      filterScrollposition: $Q('#filter') ? $Q('#filter').scrollTop : 0,
      accordionsStatusActive,
    })
  );
}

/**
 * @see handleInputsForm
 * @returns {null} stop called
 */
export function removeFilters() {
  const filters = $Qll('.js-remove-filter');
  const config = $Q('#data-config');

  if (!filters.length && !config) return;

  const { currentUrl } = config.dataset;

  if (!currentUrl) return;

  removePagination();

  filters.forEach((filter) => {
    filter.addEventListener('click', async (e) => {
      e.preventDefault();
      createLoader();

      // Validate if It has query and get it or not
      let searchParams =
        e.currentTarget.href.indexOf('?') == -1
          ? ''
          : e.currentTarget.href.split('?')[1];
      searchParams = `${searchParams}`;

      await initializeMethods({
        currentUrl,
        searchParams,
        typeFeature: !e.currentTarget.dataset.filterName
          ? 'remove_all'
          : 'remove',
        triggerData: e.currentTarget.dataset.filterName,
      });

      config.dataset.currentUrlFilters = searchParams;
    });
  });
}

/**
 * Event: listen to submit to catch input values and make URLSearchparams
 * Query: Get current url from dataset, because we need to concat filter URL
 * Structure: Structure query search with map
 * Submit: Add to new URL to window.location.href to reload broswer and new request
 * @author Daniel Santos
 */
export const onHandleSubmitSearch = () => {
  const form = $Q('.search .filter-form');

  if (!form) return;

  form.addEventListener('submit', (e) => {
    e.preventDefault();
    const config = $Q('#data-config');

    if (!config) return;

    const { currentUrl } = config.dataset;
    const formData = new FormData(e.target.closest('form'));
    const searchParams = new URLSearchParams(formData).toString();
    const paramsName = Object.keys(Shopify.queryParams);
    const paramsQuery = Object.values(Shopify.queryParams);
    const buildUrl = cleanURL(
      paramsName
        .map((query, i) => `${query}=${paramsQuery[i]}`)
        .join('&')
        .replace(/ /g, '')
    );

    window.location.href = `${window.location.origin}${currentUrl}?${buildUrl}&${searchParams}`;
  });
};

export const filtersSearching = () => {
  const listFilterWrapper = $Qll('.accordion-item');

  if (!listFilterWrapper.length) return;

  listFilterWrapper.forEach((el) => {
    const inputSearch = $Q('.js-filters-searching', el);

    if (!inputSearch) return;

    const items = $Qll('.filter-group-display__list-item', el);

    inputSearch.addEventListener('input', (e) => {
      const value = e.target.value.toLocaleLowerCase();

      items.forEach((el) => {
        const item = $Q('span:nth-of-type(2)', el)
          .textContent.trim()
          .split('(')[0]
          .toLocaleLowerCase();

        if (!item.includes(value)) {
          el.style.display = 'none';
        }

        if (item.includes(value)) {
          el.style.display = 'flex';
        }
      });
    });
  });
};

export const filtersScrollup = () => {
  const btnScrollup = $Q('#js-filters-scrollup');

  if (!btnScrollup) return;

  btnScrollup.addEventListener('click', () => {
    const filterWrap = $Q('.accordions.filter-group');

    filterWrap &&
      filterWrap.scrollIntoView({ block: 'start', behavior: 'smooth' });
  });
};
